'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import HCaptcha, { type HCaptchaHandle } from '@/components/HCaptcha';
import ContactFileUploader from '@/components/ContactFileUploader';
import LazyIframe from '@/components/LazyIframe';

export default function ContattiPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef<HCaptchaHandle>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    if (!captchaToken) {
      setFormError('Per favore, completa la verifica hCaptcha.');
      setIsSubmitting(false);
      return;
    }

    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('company', formData.company);
      body.append('subject', formData.subject);
      body.append('message', formData.message);
      body.append('website', '');
      if (attachedFile) {
        body.append('file', attachedFile);
      }
      body.append('h-captcha-response', captchaToken);

      const res = await fetch('/api/contact', { method: 'POST', body });
      const data = await res.json();

      if (!res.ok) {
        setFormError(data.error || 'Errore nell\'invio. Riprova.');
        return;
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      setAttachedFile(null);
      setFileError('');
      setCaptchaToken('');
      captchaRef.current?.reset();
    } catch {
      setFormError('Errore di connessione. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <main className="min-h-screen pt-20" id="main-content">
      {/* Header */}
      <section className="pt-12 pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <nav className="flex items-center gap-2 text-sm text-secondary-400 mb-8">
            <Link href="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-secondary-500 font-medium">Contatti</span>
          </nav>

          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-secondary-500 leading-[0.95] mb-6">
              <span className="text-gradient">Contattaci</span>
            </h1>
            <p className="text-lg sm:text-xl text-secondary-400 leading-relaxed">
              Hai un progetto in mente? Siamo qui per aiutarti a realizzarlo. 
              Contattaci per un preventivo gratuito.
            </p>
          </div>
        </div>
      </section>

      <div className="h-px bg-secondary-100" />

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl shadow-secondary-500/5 border border-secondary-100">
              <h2 className="text-2xl font-bold text-secondary-500 mb-6">
                Inviaci un messaggio
              </h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-secondary-500 mb-2">Messaggio inviato!</h3>
                  <p className="text-secondary-400 mb-6">Ti risponderemo il prima possibile.</p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="btn-outline"
                  >
                    Invia un altro messaggio
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-secondary-500 mb-2">
                        Nome e Cognome *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-secondary-500 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                        placeholder="mario@esempio.it"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-secondary-500 mb-2">
                        Telefono
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-secondary-500 mb-2">
                        Azienda
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                        placeholder="Nome Azienda Srl"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-secondary-500 mb-2">
                      Oggetto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all bg-white"
                    >
                      <option value="">Seleziona un argomento</option>
                      <option value="preventivo">Richiesta preventivo</option>
                      <option value="stampa3d">Informazioni stampa 3D</option>
                      <option value="scansione">Scansione 3D / Reverse Engineering</option>
                      <option value="progettazione">Progettazione CAD</option>
                      <option value="collaborazione">Proposta di collaborazione</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-secondary-500 mb-2">
                      Messaggio *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all resize-none"
                      placeholder="Descrivi il tuo progetto o la tua richiesta..."
                    />
                  </div>

                  <ContactFileUploader
                    file={attachedFile}
                    onFileChange={setAttachedFile}
                    error={fileError}
                    onError={setFileError}
                  />

                  {formError && (
                    <p className="text-sm text-red-600 font-medium text-center">{formError}</p>
                  )}

                  <div className="flex justify-center">
                    <HCaptcha
                      sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY || ''}
                      onVerify={setCaptchaToken}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => setFormError('Errore nella verifica hCaptcha. Riprova.')}
                      ref={captchaRef}
                      languageOverride="it"
                    />
                  </div>

                  <div className="hidden" aria-hidden="true">
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Invio in corso...
                      </span>
                    ) : (
                      'Invia messaggio'
                    )}
                  </button>

                  <p className="text-xs text-secondary-400 text-center">
                    Inviando questo modulo accetti la nostra{' '}
                    <Link href="/privacy" className="text-primary-600 underline hover:text-primary-700">
                      Privacy Policy
                    </Link>
                  </p>
                </form>
              )}
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Phone */}
                <a 
                  href="tel:+390815036695"
                  className="bg-white rounded-xl p-6 border border-secondary-100 hover:border-primary-200 hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors">
                    <svg className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-secondary-500 mb-1">Telefono</h3>
                  <p className="text-primary-600 font-medium">+39 081 503 6695</p>
                </a>

                {/* Email */}
                <a 
                  href="mailto:info@tds-med.com"
                  className="bg-white rounded-xl p-6 border border-secondary-100 hover:border-primary-200 hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors">
                    <svg className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-secondary-500 mb-1">Email</h3>
                  <p className="text-primary-600 font-medium">info@tds-med.com</p>
                </a>

                {/* Address */}
                <div className="bg-white rounded-xl p-6 border border-secondary-100">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-secondary-500 mb-1">Indirizzo</h3>
                  <p className="text-secondary-400">
                    Quinta Traversa, Via Vecchia del Bosco<br />
                    80034 Marigliano (NA)
                  </p>
                </div>

                {/* Hours */}
                <div className="bg-white rounded-xl p-6 border border-secondary-100">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-secondary-500 mb-1">Orari</h3>
                  <p className="text-secondary-400 text-sm">
                    Lun - Ven: 9:00 - 18:00<br />
                    Sab: 9:00 - 13:00
                  </p>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl overflow-hidden border border-secondary-100 shadow-lg">
                <div className="p-4 border-b border-secondary-100">
                  <h3 className="font-semibold text-secondary-500 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Dove siamo
                  </h3>
                </div>
                <div className="aspect-video relative">
                  <LazyIframe
                    src="https://www.openstreetmap.org/export/embed.html?bbox=14.4350%2C40.9200%2C14.4650%2C40.9400&layer=mapnik&marker=40.9280%2C14.4520"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mappa sede TDS Med"
                    className="absolute inset-0 bg-secondary-50"
                  />
                </div>
                <div className="p-4 bg-secondary-50">
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Quinta+Traversa+Via+Vecchia+del+Bosco+80034+Marigliano+NA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-700 text-sm font-medium hover:text-primary-800 flex items-center gap-2"
                  >
                    Apri in Google Maps
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Quick Contact CTA */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-2">Preferisci parlare di persona?</h3>
                <p className="text-primary-100 mb-6">
                  Chiamaci o vieni a trovarci in sede. Saremo felici di mostrarti i nostri lavori e discutere del tuo progetto.
                </p>
                <a 
                  href="tel:+390815036695"
                  className="inline-flex items-center gap-2 bg-white text-primary-600 font-semibold px-6 py-3 rounded-xl hover:bg-primary-50 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Chiamaci ora
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

